import { Env } from '@env';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { router, Stack, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Dimensions, Platform, ScrollView, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import type { ContractSign } from '@/api/reservations/types';
import { useContractSign } from '@/api/reservations/use-contract-sign';
import { useFinalizeReservation } from '@/api/reservations/use-finalize-reservation';
import {
  getScreenTitle,
  needsPaymentOnly,
  shouldRedirectFromContractSign,
  waitForBalanceCleared,
} from '@/components/contract-sign/utils';
import { RentalTermsSummary } from '@/components/rental-terms';
import {
  CarInfoCard,
  CostSummaryCard,
  RentalInfoCard,
  SignatureCard,
  TermsDivider,
} from '@/components/sign-and-pay/shared';
import { SignedSignaturesSection } from '@/components/signed-signatures';
import { formatNumber, isDesktop, rgbaToHex } from '@/core';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

const PAGE_WIDTH = Dimensions.get('window').width;
const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

export default function SignAndPayScreen() {
  const { theme } = useStyles(stylesheet);
  const { id, public_token } = useLocalSearchParams<{
    id: string;
    public_token: string;
  }>();

  const {
    data: resData,
    isPending: isFetchingReservation,
    refetch,
  } = useContractSign({
    variables: { id, publicToken: public_token },
    enabled: !!id,
  });

  React.useEffect(() => {
    if (shouldRedirectFromContractSign(resData)) {
      router.replace(`/trips/${id}/details`);
    }
  }, [resData, id]);

  if (isFetchingReservation) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 150, width: 150 }} />
      </View>
    );
  }

  const stripeClientSecret = resData?.stripeClientSecret ?? null;

  if (stripeClientSecret) {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          locale: 'en',
          clientSecret: stripeClientSecret,
          appearance: {
            variables: {
              colorBackground: rgbaToHex(theme.colors.background_2),
            },
            rules: {
              '.Tab': { border: 'none' },
            },
            theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
          },
        }}
      >
        <SignAndPayWithStripe
          resData={resData}
          stripeClientSecret={stripeClientSecret}
          publicToken={public_token}
          refetchReservation={refetch}
        />
      </Elements>
    );
  }

  return (
    <SignAndPayForm
      resData={resData}
      stripeClientSecret={null}
      stripe={null}
      elements={null}
      publicToken={public_token}
      refetchReservation={refetch}
    />
  );
}

function PaymentMethodCard({ borderColor }: { borderColor: string }) {
  return (
    <Card>
      <View
        style={{
          borderBottomColor: borderColor,
          borderBottomWidth: 1,
        }}
      >
        <View style={{ padding: 16 }}>
          <Text type="title2">Selected Payment Method</Text>
        </View>
      </View>

      <View style={{ padding: 16 }}>
        <PaymentElement />
      </View>
    </Card>
  );
}

function SignAndPayWithStripe({
  resData,
  stripeClientSecret,
  publicToken,
  refetchReservation,
}: {
  resData: ContractSign | undefined;
  stripeClientSecret: string;
  publicToken: string | undefined;
  refetchReservation: () => Promise<{ data?: ContractSign }>;
}) {
  const stripe = useStripe();
  const elements = useElements();

  return (
    <SignAndPayForm
      resData={resData}
      stripeClientSecret={stripeClientSecret}
      stripe={stripe}
      elements={elements}
      publicToken={publicToken}
      refetchReservation={refetchReservation}
    />
  );
}

function SignAndPayForm({
  resData,
  stripeClientSecret,
  stripe,
  elements,
  publicToken,
  refetchReservation,
}: {
  resData: ContractSign | undefined;
  stripeClientSecret: string | null;
  stripe: Stripe | null;
  elements: StripeElements | null;
  publicToken: string | undefined;
  refetchReservation: () => Promise<{ data?: ContractSign }>;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const { id } = useLocalSearchParams<{ id: string }>();

  const [signature, setSignature] = React.useState('');
  const [showValidation, setShowValidation] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [stripeError, setStripeError] = React.useState<string | null>(null);

  const isPaymentOnly = needsPaymentOnly(resData);
  const screenTitle = getScreenTitle(resData, stripeClientSecret);

  const { mutate: finalizeReservation } = useFinalizeReservation();

  const completeStripePayment = async (): Promise<boolean> => {
    if (!stripeClientSecret) return true;

    if (!stripe || !elements) {
      return false;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setStripeError(submitError.message ?? 'Payment form error');
      return false;
    }

    const { error: paymentError } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (paymentError) {
      setStripeError(paymentError.message ?? 'Payment failed');
      return false;
    }

    setStripeError(null);
    return true;
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);

    if (isPaymentOnly) {
      if (!stripeClientSecret) {
        showErrorMessage(
          'Unable to process payment. Please contact support for assistance.'
        );
        setIsSubmitting(false);
        return;
      }

      const paymentSucceeded = await completeStripePayment();
      if (!paymentSucceeded) {
        setIsSubmitting(false);
        return;
      }

      const balanceCleared = await waitForBalanceCleared(refetchReservation);
      if (!balanceCleared) {
        showErrorMessage('Payment failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      router.replace(`/trips/${id}/details`);
      return;
    }

    if (!signature) {
      setShowValidation(true);
      setIsSubmitting(false);
      return;
    }

    const paymentSucceeded = await completeStripePayment();
    if (!paymentSucceeded) {
      setIsSubmitting(false);
      return;
    }

    finalizeReservation(
      { id: Number(id), signature, credits: 0, publicToken },
      {
        onSuccess: () => {
          router.push(`/trips/${id}/details`);
        },
        onError: (err) => {
          showErrorMessage(err.message);
          setIsSubmitting(false);
        },
      }
    );
  };

  const confirmButtonText = isPaymentOnly
    ? `Pay $${formatNumber(resData?.outstandingBalance ?? 0)}`
    : 'Confirm Booking';

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: screenTitle,
          headerTitle: screenTitle,
          headerShown: !isDesktop() || Platform.OS !== 'web',
          headerStyle: { backgroundColor: theme.colors.background_1 },
          headerTintColor: theme.colors.text.primary,
          headerShadowVisible: false,
        }}
      />

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        <View style={{ flexGrow: 1, gap: 32 }}>
          <View style={{ gap: 8 }}>
            <Text type="largeTitle">{screenTitle}</Text>
            <Text type="bodyRegular">{`Rental Agreement: ${id}`}</Text>
            {isPaymentOnly && (
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                Your contract is signed. Complete payment for the remaining
                balance to confirm your booking.
              </Text>
            )}
          </View>

          <View style={styles.layout}>
            <View style={styles.leftColumn}>
              <CarInfoCard resData={resData} />
              <RentalInfoCard resData={resData} />
              <CostSummaryCard resData={resData} />
            </View>

            <View style={styles.rightColumn}>
              <View style={styles.mobileOnlyDivider} />

              <RentalTermsSummary />

              <TermsDivider />

              {!isPaymentOnly && stripeClientSecret && (
                <PaymentMethodCard
                  borderColor={theme.colors.card.default.borderColor}
                />
              )}

              {isPaymentOnly ? (
                <View style={{ gap: 16 }}>
                  <SignedSignaturesSection resData={resData} />
                </View>
              ) : (
                <SignatureCard
                  onDone={(sig) => {
                    setSignature(sig);
                    setShowValidation(false);
                  }}
                  onClear={() => setSignature('')}
                />
              )}

              {isPaymentOnly && stripeClientSecret && (
                <PaymentMethodCard
                  borderColor={theme.colors.card.default.borderColor}
                />
              )}

              {isPaymentOnly && !stripeClientSecret && (
                <Text type="caption1" style={{ color: theme.colors.error }}>
                  Payment is unavailable. Please contact support for assistance.
                </Text>
              )}

              <View style={{ gap: 8 }}>
                <Button
                  type="primary"
                  text={confirmButtonText}
                  onPress={handleConfirm}
                  loading={isSubmitting}
                  disabled={isPaymentOnly && !stripeClientSecret}
                  style={{ width: '100%' }}
                />

                {showValidation && !signature && !isPaymentOnly && (
                  <Text type="caption1" style={{ color: theme.colors.error }}>
                    *Signature is required
                  </Text>
                )}

                {stripeError && (
                  <Text type="caption1" style={{ color: theme.colors.error }}>
                    {stripeError}
                  </Text>
                )}
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  scrollContent: {
    width: {
      xs: PAGE_WIDTH - 32,
      md: 1080,
    },
    alignSelf: 'center',
    paddingBottom: 32,
  },
  layout: {
    gap: 16,
    flexDirection: {
      md: 'row-reverse',
    },
    alignItems: {
      md: 'flex-start',
    },
  },
  leftColumn: {
    gap: 16,
    flex: {
      md: 1,
    },
  },
  rightColumn: {
    gap: 16,
    width: {
      md: '50%',
    },
  },
  mobileOnlyDivider: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    height: 1,
    marginTop: 24,
    marginBottom: 16,
    backgroundColor: theme.colors.card.default.borderColor,
  },
}));
